import { SWAPTION_DIRECTIONS } from "@/lib/interfaces and Enums/ENUMOptionTradeMessage";
import { returnSwaptionTemplateChanges } from "../helper/swapTemplateRule";
import { ITicketFieldsProps } from "../helper/ticketSharedHelper";
import SwapTemplateSelect from "../ticketFields/SwapTemplateSelect";
import DirectionSelect from "../ticketFields/DirectionSelect";
import NominalInput from "../ticketFields/NominalInput";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketRow from "../ticketFields/TicketRow";
import AllocationSplits from "../ticketFields/AllocationSplits";
import TicketExternalIdRow from "../ticketFields/TicketExternalIdRow";
import { useSwaptionTicket } from "./useSwaptionTicket";
import { TicketInstrument } from "@/store/tradeFromScratchStore";
import { returnSettlementHolidayCalendars } from "../helper/holidayCalendarsHelper";

// The swaption fields a Trade Creation leg row shows inline. Option terms and the
// underlying swap live in SwaptionDetailFields.
const SwaptionCoreFields = ({
  ticketId,
  highlightedKeys,
}: ITicketFieldsProps) => {
  const { ticket, updateSwaptionTicket, requiredKeys } =
    useSwaptionTicket(ticketId);

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);

  return (
    <div
      data-testid="SwaptionCoreFieldsContainer"
      id="SwaptionCoreFieldsContainer"
      className="flex flex-col gap-2"
    >
      {/* Underlying template, unfiltered: Direction does not say ZCS (spec). Also sets
          Strike from the template's latest yield. */}
      <TicketRow label="Underlying" testId="SwaptionTicketTemplateRow">
        <SwapTemplateSelect
          testId="SwaptionTicketTemplateInput"
          value={ticket.templateName}
          onSelect={(template) =>
            updateSwaptionTicket(returnSwaptionTemplateChanges(ticket, template))
          }
        />
      </TicketRow>

      <TicketRow
        label="Nominal"
        testId="SwaptionTicketNominalRow"
        isHighlighted={isMissing("nominal")}
        isRequired={isRequired("nominal")}
      >
        <NominalInput
          testId="SwaptionTicketNominalInput"
          value={ticket.nominal}
          onCommit={(nominal) => updateSwaptionTicket({ nominal })}
        />
      </TicketRow>

      <AllocationSplits
        testIdPrefix="SwaptionTicket"
        portfolioCode={ticket.portfolioCode}
        onPortfolioChange={(portfolioCode) => updateSwaptionTicket({ portfolioCode })}
        isPortfolioRequired={isRequired("portfolioCode")}
        total={ticket.nominal}
        splits={ticket.additionalAllocations}
        sizeKind="nominal"
        highlightedKeys={highlightedKeys}
        onChange={(additionalAllocations) => updateSwaptionTicket({ additionalAllocations })}
      />

      {/* Buy / Sell Payer / Receiver, which also sets the option type (spec F10). */}
      <TicketRow
        label="Direction"
        testId="SwaptionTicketDirectionRow"
        isHighlighted={isMissing("direction")}
        isRequired={isRequired("direction")}
      >
        <DirectionSelect
          testId="SwaptionTicketDirectionInput"
          value={ticket.direction}
          directions={SWAPTION_DIRECTIONS}
          onChange={(direction) => updateSwaptionTicket({ direction })}
        />
      </TicketRow>

      <TicketRow
        label="Settlement Date"
        testId="SwaptionTicketSettlementDateRow"
        isHighlighted={isMissing("settlementDate")}
        isRequired={isRequired("settlementDate")}
      >
        <TicketDateInput
          testId="SwaptionTicketSettlementDateInput"
          holidayCalendars={returnSettlementHolidayCalendars(TicketInstrument.Swaption, ticket)}
          value={ticket.settlementDate}
          onChange={(settlementDate) =>
            updateSwaptionTicket({ settlementDate })
          }
        />
      </TicketRow>

      <TicketExternalIdRow
        testIdPrefix="SwaptionTicket"
        value={ticket.transactionNumber}
        onChange={(transactionNumber) => updateSwaptionTicket({ transactionNumber })}
      />
    </div>
  );
};

export default SwaptionCoreFields;
